import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useStore } from 'react-redux';
import { updateTutorialState } from 'redux/slices/systemSlice';
import TooltipComponent from './TutorialTooltipComponent';
import {
  positionTutorialSpotlight,
  resolveTutorialTarget
} from './tutorialSpotlightPosition';

const TutorialContext = createContext();

export const TutorialProvider = ({ children }) => {
  const reduxStore = useStore();
  const [steps, setSteps] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTutorialKey, setCurrentTutorialKey] = useState(null);
  const activeStepIndexRef = useRef(0);
  const stepsRef = useRef([]);

  const syncSpotlight = useCallback((stepIndex = activeStepIndexRef.current) => {
    const step = stepsRef.current[stepIndex];
    if (!step) {
      return;
    }

    const target = resolveTutorialTarget(step.target);
    if (target) {
      positionTutorialSpotlight(target, step.spotlightPadding ?? 8);
    }
  }, []);

  const startTutorial = useCallback(
    (tutorialSteps, tutorialKey) => {
      stepsRef.current = tutorialSteps;
      activeStepIndexRef.current = 0;
      setSteps(tutorialSteps);
      setIsRunning(true);
      setCurrentTutorialKey(tutorialKey);
      reduxStore.dispatch(
        updateTutorialState({ name: tutorialKey, status: 'running' })
      );
    },
    [reduxStore]
  );

  const stopTutorial = useCallback(
    (tutorialKey) => {
      setIsRunning(false);
      setSteps([]);
      stepsRef.current = [];
      reduxStore.dispatch(
        updateTutorialState({ name: tutorialKey, status: 'stopped' })
      );
    },
    [reduxStore]
  );

  const saveTutorialStatus = (tutorialKey, status) => {
    const hasSeenTutorial = status === 'finished' || status === 'skipped';
    const updatedTutorial = { tutorial: tutorialKey, hasSeenTutorial };

    const storedTutorials = JSON.parse(localStorage.getItem('tutorials')) || [];
    const tutorialIndex = storedTutorials.findIndex(
      (t) => t.tutorial === tutorialKey
    );

    if (tutorialIndex !== -1) {
      storedTutorials[tutorialIndex].hasSeenTutorial = hasSeenTutorial;
    } else {
      storedTutorials.push(updatedTutorial);
    }

    localStorage.setItem('tutorials', JSON.stringify(storedTutorials));
  };

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }

    const handleReposition = () => syncSpotlight();

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    const intervalId = window.setInterval(handleReposition, 200);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      window.clearInterval(intervalId);
    };
  }, [isRunning, syncSpotlight]);

  const handleJoyrideCallback = useCallback(
    (data) => {
      const { status, action, index, type, lifecycle } = data;

      if (typeof index === 'number') {
        activeStepIndexRef.current = index;
      }

      if (
        lifecycle === 'complete' &&
        (type === EVENTS.TOUR_START ||
          type === EVENTS.STEP_BEFORE ||
          type === EVENTS.STEP_AFTER)
      ) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => syncSpotlight(index));
        });
      }

      if (status === STATUS.FINISHED || action === ACTIONS.SKIP) {
        saveTutorialStatus(currentTutorialKey, status);
        stopTutorial(currentTutorialKey);
      }
    },
    [currentTutorialKey, stopTutorial, syncSpotlight]
  );

  const contextValue = useMemo(
    () => ({
      startTutorial
    }),
    [startTutorial]
  );

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}
      <Joyride
        steps={steps}
        run={isRunning}
        continuous
        showSkipButton
        disableBeacon
        disableScrolling
        disableScrollParentFix
        scrollToFirstStep={false}
        spotlightPadding={8}
        tooltipComponent={(props) => (
          <TooltipComponent {...props} steps={steps} />
        )}
        callback={handleJoyrideCallback}
        styles={{
          spotlight: {
            position: 'fixed',
            zIndex: 10000,
            boxSizing: 'border-box'
          },
          overlay: {
            overflow: 'hidden !important',
            fontFamily: 'YekanBakhFaNum'
          },
          options: {
            zIndex: 10000,
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            fontFamily: 'YekanBakhFaNum'
          },
          tooltip: {
            backgroundColor: '#fff',
            borderRadius: '5rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            padding: '1rem',
            textAlign: 'center',
            maxWidth: '20rem',
            fontFamily: 'YekanBakhFaNum',
            fontSize: '1rem'
          }
        }}
        locale={{
          back: 'Return',
          close: 'Close',
          last: 'End',
          next: 'Next',
          skip: 'Skip'
        }}
      />
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = () => useContext(TutorialContext);
